import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';

interface BulkCancelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onSubmit: (reason: string) => Promise<void>;
  isSubmitting: boolean;
}

/** Asks for the reason recorded on every task a bulk cancel closes. */
export function BulkCancelDialog({
  open,
  onOpenChange,
  ...formProps
}: BulkCancelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {/* The form mounts with the dialog content, so each opening starts with an empty reason. */}
        <BulkCancelForm onClose={() => onOpenChange(false)} {...formProps} />
      </DialogContent>
    </Dialog>
  );
}

function BulkCancelForm({
  selectedCount,
  onSubmit,
  isSubmitting,
  onClose,
}: Omit<BulkCancelDialogProps, 'open' | 'onOpenChange'> & {
  onClose: () => void;
}) {
  const gt = useGT();
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="dialog title: cancel several selected tasks">
            Cancel tasks
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="dialog description for bulk task cancellation">
            Cancel <Var>{selectedCount}</Var> selected tasks. They move to Done
            with this reason and keep their history.
          </T>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-2">
        <Label htmlFor="bulk-cancel-reason">
          <T context="label: why the tasks are being cancelled">Reason</T>
        </Label>
        <Textarea
          id="bulk-cancel-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={gt('e.g. Keyed by hand in Famous', {
            context: 'placeholder for a bulk task cancellation reason',
          })}
          rows={3}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          <T context="button: close the dialog without cancelling tasks">
            Keep Tasks
          </T>
        </Button>
        <Button
          variant="destructive"
          onClick={() => {
            void onSubmit(trimmed);
          }}
          disabled={isSubmitting || trimmed.length === 0}
        >
          {isSubmitting
            ? gt('Cancelling...', {
                context: 'button label while tasks are being cancelled',
              })
            : gt('Cancel {count} Tasks', {
                count: String(selectedCount),
                context: 'button: confirm cancelling the selected tasks',
              })}
        </Button>
      </DialogFooter>
    </>
  );
}
